import { useState, useEffect, useRef, type ChangeEvent, type FormEvent, type MouseEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, X, Image as ImageIcon, MapPin, Calendar, Clock, Euro, Upload, Users, Check } from 'lucide-react';
import Logo from '../components/Logo';
import { Outing, PaymentMethod } from '../types';
import { outingsService } from '../../../services/jacanaDbService';
import { organizationService, DEFAULT_ORGANIZATION_ID } from '../../../services/organizationService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../services/firebase';
import { toast } from 'react-hot-toast';
import HelpTrigger from '../../help/components/HelpTrigger';
import ConfirmDialog from '../components/ConfirmDialog';
import { isValidBelgianIban, normalizeIban } from '../../../utils/iban';
import { validatePricingConfig, type PricingConfig } from '../pricing/pricingEngine';

function normalizePriceInput(value: string): number {
  const normalizedValue = value.replace(',', '.').trim();
  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
}

type SpecialRateFormState = {
  rateId: string;
  label: string;
  unitPrice: number;
  enabled: boolean;
};

type PricingFormState = {
  normalUnitPrice: number;
  freeChildEnabled: boolean;
  freeChildMaxAgeExclusive: number;
  freeChildLabel: string;
  specialRates: SpecialRateFormState[];
  nextSpecialRateIndex: number;
};

const DEFAULT_NORMAL_RATE_LABEL = 'Tarif normal';
const DEFAULT_FREE_CHILD_MAX_AGE = 7;
const DEFAULT_LATE_BOOKING_HOURS_BEFORE = 4;

function buildDefaultFreeChildLabel(maxAgeExclusive: number): string {
  return `Enfants de moins de ${maxAgeExclusive} ans`;
}

function createSpecialRateDraft(index: number): SpecialRateFormState {
  return {
    rateId: `special_${index}`,
    label: '',
    unitPrice: 0,
    enabled: true,
  };
}

function getNextSpecialRateIndex(specialRates: Array<{ rateId: string }>): number {
  const usedIndexes = specialRates
    .map((rate) => Number.parseInt(rate.rateId.replace('special_', ''), 10))
    .filter((value) => Number.isInteger(value) && value > 0);

  return usedIndexes.length > 0 ? Math.max(...usedIndexes) + 1 : specialRates.length + 1;
}

function buildPricingFormState(pricingConfig?: PricingConfig): PricingFormState {
  if (pricingConfig) {
    return {
      normalUnitPrice: pricingConfig.normalRate.unitPrice,
      freeChildEnabled: pricingConfig.freeChildRule.enabled,
      freeChildMaxAgeExclusive: pricingConfig.freeChildRule.maxAgeExclusive,
      freeChildLabel: pricingConfig.freeChildRule.label,
      specialRates: pricingConfig.specialRates.map((rate) => ({
        rateId: rate.rateId,
        label: rate.label,
        unitPrice: rate.unitPrice,
        enabled: rate.enabled,
      })),
      nextSpecialRateIndex: getNextSpecialRateIndex(pricingConfig.specialRates),
    };
  }

  return {
    normalUnitPrice: 5,
    freeChildEnabled: false,
    freeChildMaxAgeExclusive: DEFAULT_FREE_CHILD_MAX_AGE,
    freeChildLabel: buildDefaultFreeChildLabel(DEFAULT_FREE_CHILD_MAX_AGE),
    specialRates: [],
    nextSpecialRateIndex: 1,
  };
}

function getPricingValidationError(pricingForm: PricingFormState): string | null {
  if (!(pricingForm.normalUnitPrice > 0)) {
    return 'Le tarif normal doit être supérieur à 0.';
  }

  if (pricingForm.freeChildEnabled && !(pricingForm.freeChildMaxAgeExclusive > 0)) {
    return 'L\'âge maximum enfant doit être supérieur à 0.';
  }

  if (pricingForm.specialRates.length > 3) {
    return 'Vous ne pouvez pas ajouter plus de 3 tarifs spéciaux.';
  }

  for (const specialRate of pricingForm.specialRates) {
    if (!specialRate.enabled) {
      continue;
    }

    if (!specialRate.label.trim()) {
      return 'Chaque tarif spécial activé doit avoir un libellé.';
    }

    if (!(specialRate.unitPrice > 0)) {
      return 'Chaque tarif spécial activé doit avoir un prix supérieur à 0.';
    }
  }

  return null;
}

function buildPricingConfigFromForm(pricingForm: PricingFormState): PricingConfig {
  const freeChildMaxAgeExclusive = pricingForm.freeChildMaxAgeExclusive > 0
    ? pricingForm.freeChildMaxAgeExclusive
    : DEFAULT_FREE_CHILD_MAX_AGE;
  const freeChildLabel = pricingForm.freeChildLabel.trim() || buildDefaultFreeChildLabel(freeChildMaxAgeExclusive);
  const specialRates = pricingForm.specialRates
    .filter((rate) => rate.enabled || rate.label.trim() || rate.unitPrice > 0)
    .map((rate) => ({
      rateId: rate.rateId,
      label: rate.label.trim(),
      unitPrice: rate.unitPrice,
      enabled: rate.enabled,
    }));

  return validatePricingConfig({
    currency: 'EUR',
    normalRate: {
      rateId: 'normal',
      label: DEFAULT_NORMAL_RATE_LABEL,
      unitPrice: pricingForm.normalUnitPrice,
      enabled: true,
    },
    freeChildRule: {
      enabled: pricingForm.freeChildEnabled,
      rateId: 'free_child_under_age',
      label: freeChildLabel,
      maxAgeExclusive: freeChildMaxAgeExclusive,
      unitPrice: 0,
    },
    specialRates,
    version: 'v1',
  });
}

function isProjectFirebaseStorageUrl(imageUrl: string, bucketName?: string): boolean {
  const normalizedUrl = imageUrl.trim();
  if (!normalizedUrl) return false;

  const normalizedBucketName = (bucketName || '').trim();
  if (normalizedBucketName && normalizedUrl.startsWith(`gs://${normalizedBucketName}/`)) {
    return true;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

    if (parsedUrl.hostname === 'firebasestorage.googleapis.com') {
      return pathSegments[0] === 'v0'
        && pathSegments[1] === 'b'
        && pathSegments[2] === normalizedBucketName
        && pathSegments[3] === 'o';
    }

    if (parsedUrl.hostname === 'storage.googleapis.com') {
      return pathSegments[0] === normalizedBucketName
        || (
          pathSegments[0] === 'download'
          && pathSegments[1] === 'storage'
          && pathSegments[2] === 'v1'
          && pathSegments[3] === 'b'
          && pathSegments[4] === normalizedBucketName
          && pathSegments[5] === 'o'
        );
    }
  } catch {
    return false;
  }

  return false;
}

export default function CreateOuting() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [gearItem, setGearItem] = useState('');
  const [organizationDefaultBankAccount, setOrganizationDefaultBankAccount] = useState('');
  const [initialFormSnapshot, setInitialFormSnapshot] = useState('');
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [leaveAfterSave, setLeaveAfterSave] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | 'history-back' | null>(null);
  const skipNextPopstateGuardRef = useRef(false);
  const [uploadSeed] = useState(() => Date.now());
  const uploadCounterRef = useRef(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    maxParticipants: 10,
    duration: '',
    difficulty: 'Facile' as 'Facile' | 'Moyen' | 'Difficile',
    location: '',
    meetingPoint: '',
    requiredGear: [] as string[],
    imageUrl: '',
    paymentMethods: ['mollie'] as PaymentMethod[],
    bankTransferTemplate: '',
    bankAccountOverride: '',
    pmrAccessible: undefined as boolean | undefined,
    strollerAccessible: undefined as boolean | undefined,
    dogsAllowedOnLeash: undefined as boolean | undefined,
    allowLateBookings: false,
    lateBookingHoursBefore: 4,
    pricingForm: buildPricingFormState(),
  });

  useEffect(() => {
    async function load() {
      try {
        const [organization, data] = await Promise.all([
          organizationService.getById().catch(() => null),
          isEdit ? outingsService.getById(id) : Promise.resolve(null),
        ]);

        const defaultIban = organization?.paymentSettings?.bankAccount || '';
        setOrganizationDefaultBankAccount(defaultIban);

        if (isEdit && data) {
          const hydratedFormData = {
            title: data.title,
            description: data.description,
            date: data.date,
            time: '',
            duration: data.duration,
            difficulty: data.difficulty,
            maxParticipants: data.maxParticipants,
            location: data.location,
            meetingPoint: data.meetingPoint,
            requiredGear: data.requiredGear,
            imageUrl: data.imageUrl,
            paymentMethods: Array.from(new Set((data.paymentMethods || ['mollie']).map(m => String(m) === 'stripe' ? 'mollie' : m))) as PaymentMethod[],
            bankTransferTemplate: data.bankTransferTemplate || '',
            bankAccountOverride: data.bankAccountOverride || defaultIban,
            pmrAccessible: typeof data.pmrAccessible === 'boolean' ? data.pmrAccessible : undefined,
            strollerAccessible: typeof data.strollerAccessible === 'boolean' ? data.strollerAccessible : undefined,
            dogsAllowedOnLeash: typeof data.dogsAllowedOnLeash === 'boolean' ? data.dogsAllowedOnLeash : undefined,
            allowLateBookings: data.allowLateBookings ?? false,
            lateBookingHoursBefore:
              typeof data.lateBookingHoursBefore === 'number'
              && data.lateBookingHoursBefore > 0
              && data.lateBookingHoursBefore < 24
                ? data.lateBookingHoursBefore
                : DEFAULT_LATE_BOOKING_HOURS_BEFORE,
            pricingForm: buildPricingFormState(data.pricingConfig),
          };
          setFormData(hydratedFormData);
          setInitialFormSnapshot(JSON.stringify(hydratedFormData));
          return;
        }

        const draftFormData = {
          title: '',
          description: '',
          date: '',
          time: '',
          maxParticipants: 10,
          duration: '',
          difficulty: 'Facile' as 'Facile' | 'Moyen' | 'Difficile',
          location: '',
          meetingPoint: '',
          requiredGear: [] as string[],
          imageUrl: '',
          paymentMethods: ['mollie'] as PaymentMethod[],
          bankTransferTemplate: '',
          bankAccountOverride: defaultIban,
          pmrAccessible: undefined as boolean | undefined,
          strollerAccessible: undefined as boolean | undefined,
          dogsAllowedOnLeash: undefined as boolean | undefined,
          allowLateBookings: false,
          lateBookingHoursBefore: DEFAULT_LATE_BOOKING_HOURS_BEFORE,
          pricingForm: buildPricingFormState(),
        };
        setFormData(draftFormData);
        setInitialFormSnapshot(JSON.stringify(draftFormData));
      } catch {
        toast.error('Erreur lors du chargement des parametres de paiement.');
      }
    }

    load();
  }, [id, isEdit]);

  const hasIbanInput = formData.bankAccountOverride.trim().length > 0;
  const hasValidIbanInput = !hasIbanInput || isValidBelgianIban(formData.bankAccountOverride);
  const storageBucketName = storage.app.options.storageBucket || '';
  const showsExternalImageWarning = formData.imageUrl.trim().length > 0
    && !isProjectFirebaseStorageUrl(formData.imageUrl, storageBucketName);
  const hasUnsavedChanges =
    initialFormSnapshot.length > 0
    && JSON.stringify(formData) !== initialFormSnapshot
    && !loading;

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    window.history.pushState({ guardUnsaved: true }, '', window.location.href);

    const handlePopState = () => {
      if (skipNextPopstateGuardRef.current) {
        skipNextPopstateGuardRef.current = false;
        return;
      }

      setPendingNavigation('history-back');
      setShowUnsavedChangesDialog(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const setBooleanField = (
    field: 'pmrAccessible' | 'strollerAccessible' | 'dogsAllowedOnLeash',
    value: boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updatePricingForm = (updater: (pricingForm: PricingFormState) => PricingFormState) => {
    setFormData((prev) => {
      const nextPricingForm = updater(prev.pricingForm);
      return {
        ...prev,
        pricingForm: nextPricingForm,
      };
    });
  };

  const addSpecialRate = () => {
    updatePricingForm((prev) => {
      if (prev.specialRates.length >= 3) {
        return prev;
      }

      return {
        ...prev,
        specialRates: [...prev.specialRates, createSpecialRateDraft(prev.nextSpecialRateIndex)],
        nextSpecialRateIndex: prev.nextSpecialRateIndex + 1,
      };
    });
  };

  const removeSpecialRate = (rateId: string) => {
    updatePricingForm((prev) => ({
      ...prev,
      specialRates: prev.specialRates.filter((rate) => rate.rateId !== rateId),
    }));
  };

  const updateSpecialRate = (rateId: string, updates: Partial<SpecialRateFormState>) => {
    updatePricingForm((prev) => ({
      ...prev,
      specialRates: prev.specialRates.map((rate) => rate.rateId === rateId ? { ...rate, ...updates } : rate),
    }));
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    try {
      setImageUploading(true);
      const fileExtension = file.name.split('.').pop();
      uploadCounterRef.current += 1;
      const fileName = `outings/${uploadSeed}_${uploadCounterRef.current}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setFormData({ ...formData, imageUrl: downloadURL });
      toast.success('Image chargée avec succès !');
    } catch (error) {
      console.error("Erreur lors du chargement de l'image:", error);
      toast.error("Erreur lors du chargement de l'image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!hasValidIbanInput) {
      toast.error('IBAN belge invalide.');
      return;
    }

    const normalizedOverride = formData.bankAccountOverride.trim()
      ? normalizeIban(formData.bankAccountOverride)
      : '';

    if (formData.paymentMethods.includes('bank_transfer') && !normalizedOverride && !organizationDefaultBankAccount) {
      toast.error('Ajoutez un compte IBAN valide pour activer le virement bancaire.');
      return;
    }

    if (!isEdit) {
      const hasMissingAccessibilityChoice =
        typeof formData.pmrAccessible !== 'boolean' ||
        typeof formData.strollerAccessible !== 'boolean' ||
        typeof formData.dogsAllowedOnLeash !== 'boolean';

      if (hasMissingAccessibilityChoice) {
        toast.error('Veuillez renseigner tous les champs du bloc Accessibilité et accueil.');
        return;
      }
    }

    const pricingValidationError = getPricingValidationError(formData.pricingForm);
    if (pricingValidationError) {
      toast.error(pricingValidationError);
      return;
    }

    if (formData.allowLateBookings) {
      const lateBookingHoursBefore = Number.parseInt(String(formData.lateBookingHoursBefore), 10);
      if (!Number.isInteger(lateBookingHoursBefore) || lateBookingHoursBefore <= 0 || lateBookingHoursBefore >= 24) {
        toast.error('Le délai de réservation de dernière minute doit être un entier entre 1 et 23 heures.');
        return;
      }
    }

    setLoading(true);
    try {
      const pricingConfig = buildPricingConfigFromForm(formData.pricingForm);
      const payload = Object.fromEntries(
        Object.entries({
        ...Object.fromEntries(Object.entries(formData).filter(([key]) => key !== 'pricingForm')),
        pricingConfig,
        bankAccountOverride: normalizedOverride,
        organizationId: DEFAULT_ORGANIZATION_ID,
        }).filter(([, value]) => value !== undefined)
      );

      if (isEdit) {
        await outingsService.update(id, payload as Partial<Outing>);
        toast.success("Sortie mise à jour !");
      } else {
        await outingsService.create({
          ...(payload as Omit<Outing, 'id'>),
          currentParticipants: 0,
          createdAt: new Date().toISOString(),
          publicationStatus: 'draft',
          publishedAt: null,
          publishedByUid: null,
          publicationAnnouncementStatus: 'not_started',
          publicationAnnouncementSentAt: null,
        });
        toast.success("Brouillon enregistré. La sortie devra être publiée plus tard.");
      }

      setInitialFormSnapshot(JSON.stringify(formData));
      if (leaveAfterSave) {
        const destination = pendingNavigation;
        setLeaveAfterSave(false);
        setPendingNavigation(null);
        if (destination === 'history-back') {
          skipNextPopstateGuardRef.current = true;
          window.history.back();
          return;
        }
        if (destination) {
          navigate(destination);
          return;
        }
      }

      navigate('/admin');
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentMethod = (method: PaymentMethod) => {
    setFormData(prev => {
      const methods = prev.paymentMethods.includes(method)
        ? prev.paymentMethods.filter(m => m !== method)
        : [...prev.paymentMethods, method];
      
      if (methods.length === 0) return prev;

      // Handle template initialization
      let newTemplate = prev.bankTransferTemplate;
      if (method === 'bank_transfer' && !prev.paymentMethods.includes('bank_transfer') && !newTemplate) {
        const dateObj = prev.date ? new Date(prev.date) : new Date();
        const yymmjj = dateObj.toISOString().slice(2, 10).replace(/-/g, '');
        newTemplate = `${yymmjj} - ${prev.location || '[Lieu]'} + #RESERVATION_ID# + #NB_PERSONNES#`;
      }

      return { ...prev, paymentMethods: methods, bankTransferTemplate: newTemplate };
    });
  };

  const addGear = () => {
    if (gearItem.trim()) {
      setFormData({ ...formData, requiredGear: [...formData.requiredGear, gearItem.trim()] });
      setGearItem('');
    }
  };

  const removeGear = (index: number) => {
    setFormData({ ...formData, requiredGear: formData.requiredGear.filter((_, i) => i !== index) });
  };

  const handleBackToAdmin = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isEdit || !hasUnsavedChanges) return;

    event.preventDefault();
    setPendingNavigation('/admin');
    setShowUnsavedChangesDialog(true);
  };

  const handleDiscardChanges = () => {
    setLeaveAfterSave(false);
    setShowUnsavedChangesDialog(false);

    if (pendingNavigation === 'history-back') {
      setPendingNavigation(null);
      skipNextPopstateGuardRef.current = true;
      window.history.back();
      return;
    }

    navigate(pendingNavigation || '/admin');
    setPendingNavigation(null);
  };

  const handleSaveFromDialog = () => {
    setLeaveAfterSave(true);
    const formElement = document.getElementById('outing-form') as HTMLFormElement | null;
    formElement?.requestSubmit();
  };

  const handleCloseUnsavedChangesDialog = () => {
    setLeaveAfterSave(false);
    setShowUnsavedChangesDialog(false);
    if (pendingNavigation === 'history-back' && hasUnsavedChanges) {
      window.history.pushState({ guardUnsaved: true }, '', window.location.href);
    }
    setPendingNavigation(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pt-32 md:pt-36 pb-20">
      <Link
        to="/admin"
        onClick={handleBackToAdmin}
        className="group inline-flex items-center gap-4 text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] opacity-60 hover:opacity-100 transition-all mb-16 min-h-[44px]"
      >
        <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform duration-500" />
        Retour à l'administration
      </Link>

      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-white p-6 sm:p-10 md:p-24 rounded-[80px] border border-brand-primary/5 shadow-2xl relative overflow-hidden"
      >
        <div className="absolute -right-20 -top-20 opacity-[0.02] scale-[2] -rotate-12 pointer-events-none">
          <Logo iconOnly />
        </div>

        <div className="space-y-4 mb-16">
          <h4 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-accent">Configuration</h4>
          <h1 className="text-5xl md:text-6xl font-serif font-bold italic leading-tight text-brand-primary">
            {isEdit ? 'Modifier l\'expédition' : 'Nouvelle expédition'}
          </h1>
        </div>

        <form id="outing-form" onSubmit={handleSubmit} className="space-y-8 md:space-y-16">
          <div className="space-y-12">
            <div className="group">
              <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Titre de l'expérience</label>
              <input 
                required
                type="text" 
                className="w-full bg-transparent border-b border-brand-primary/20 py-6 text-2xl md:text-3xl font-serif italic focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/40 text-brand-primary"
                placeholder="Ex: Immersion en forêt primaire"
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16">
              <div className="group">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Date & Heure</label>
                  <HelpTrigger
                    articleId="outing.create.schedule"
                    category="outing.create"
                    label="Aide sur la date et l’heure"
                  />
                </div>
                <div className="relative">
                  <Calendar className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                  <input 
                    required
                    type="datetime-local" 
                    className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                    value={formData.date}
                    onChange={e => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              </div>
              <div className="group">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Durée estimée</label>
                <div className="relative">
                  <Clock className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                  <input 
                    required
                    type="text" 
                    className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/45 text-brand-primary"
                    placeholder="Ex: 3h"
                    value={formData.duration}
                    onChange={e => setFormData({...formData, duration: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:gap-16">
              <div className="group">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Niveau d'engagement</label>
                <select 
                  className="w-full bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all appearance-none cursor-pointer text-brand-primary"
                  value={formData.difficulty}
                  onChange={e => setFormData({...formData, difficulty: e.target.value as 'Facile' | 'Moyen' | 'Difficile'})}
                >
                  <option value="Facile">Facile</option>
                  <option value="Moyen">Moyen</option>
                  <option value="Difficile">Difficile</option>
                </select>
              </div>
            </div>

            <div className="group bg-brand-paper/50 p-8 rounded-[32px] border border-brand-primary/5 space-y-8">
              <div className="flex items-center justify-between gap-4">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-80 block text-brand-primary">
                  Tarification
                </label>
                <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-55 text-brand-primary">
                  Devise EUR · version v1
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16">
                <div className="group">
                  <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                    Tarif normal (€)
                  </label>
                  <div className="relative">
                    <Euro className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                    <input
                      required
                      aria-label="Tarif normal (€)"
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-2xl font-serif italic focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                      value={formData.pricingForm.normalUnitPrice}
                      onChange={(e) => updatePricingForm((prev) => ({
                        ...prev,
                        normalUnitPrice: normalizePriceInput(e.target.value),
                      }))}
                    />
                  </div>
                </div>

              </div>

              <div className="space-y-4 rounded-[24px] border border-brand-primary/10 bg-white/70 px-6 py-5">
                <label className="inline-flex items-center gap-3 text-[12px] md:text-sm font-semibold text-brand-primary">
                  <input
                    type="checkbox"
                    aria-label="Activer le tarif enfant gratuit"
                    checked={formData.pricingForm.freeChildEnabled}
                    onChange={(e) => updatePricingForm((prev) => ({
                      ...prev,
                      freeChildEnabled: e.target.checked,
                      freeChildLabel: prev.freeChildLabel.trim() || buildDefaultFreeChildLabel(prev.freeChildMaxAgeExclusive),
                    }))}
                  />
                  Activer l'option enfant gratuit
                </label>

                {formData.pricingForm.freeChildEnabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                        Âge maximum exclusif
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        aria-label="Âge maximum enfant gratuit"
                        className="w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                        value={formData.pricingForm.freeChildMaxAgeExclusive}
                        onChange={(e) => updatePricingForm((prev) => {
                          const nextAge = Math.max(0, Number.parseInt(e.target.value || '0', 10) || 0);
                          const currentGeneratedLabel = buildDefaultFreeChildLabel(prev.freeChildMaxAgeExclusive);
                          const nextGeneratedLabel = buildDefaultFreeChildLabel(nextAge || DEFAULT_FREE_CHILD_MAX_AGE);

                          return {
                            ...prev,
                            freeChildMaxAgeExclusive: nextAge,
                            freeChildLabel: !prev.freeChildLabel.trim() || prev.freeChildLabel === currentGeneratedLabel
                              ? nextGeneratedLabel
                              : prev.freeChildLabel,
                          };
                        })}
                      />
                    </div>

                    <div className="group">
                      <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                        Libellé affiché
                      </label>
                      <input
                        type="text"
                        aria-label="Libellé tarif enfant gratuit"
                        className="w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold tracking-[0.04em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary placeholder:text-brand-primary/40"
                        placeholder={buildDefaultFreeChildLabel(formData.pricingForm.freeChildMaxAgeExclusive || DEFAULT_FREE_CHILD_MAX_AGE)}
                        value={formData.pricingForm.freeChildLabel}
                        onChange={(e) => updatePricingForm((prev) => ({
                          ...prev,
                          freeChildLabel: e.target.value,
                        }))}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 text-brand-primary">
                      Tarifs spéciaux
                    </p>
                    <p className="text-[12px] md:text-sm text-brand-primary/65 mt-2">
                      Jusqu'à 3 tarifs spéciaux avec identifiants stables générés automatiquement.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addSpecialRate}
                    disabled={formData.pricingForm.specialRates.length >= 3}
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-brand-primary/15 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary hover:border-brand-accent hover:text-brand-accent transition-all disabled:opacity-40"
                  >
                    <Plus size={14} />
                    Ajouter un tarif spécial
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.pricingForm.specialRates.map((specialRate, index) => (
                    <div key={specialRate.rateId} className="rounded-[24px] border border-brand-primary/10 bg-white/70 p-6 space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-65 text-brand-primary">
                          {specialRate.rateId}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeSpecialRate(specialRate.rateId)}
                          className="inline-flex items-center gap-2 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/60 hover:text-brand-accent transition-all"
                        >
                          <X size={14} />
                          Supprimer
                        </button>
                      </div>

                      <label className="inline-flex items-center gap-3 text-[12px] md:text-sm font-semibold text-brand-primary">
                        <input
                          type="checkbox"
                          aria-label={`Activer le tarif spécial ${index + 1}`}
                          checked={specialRate.enabled}
                          onChange={(e) => updateSpecialRate(specialRate.rateId, { enabled: e.target.checked })}
                        />
                        Tarif spécial actif
                      </label>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                            Libellé
                          </label>
                          <input
                            type="text"
                            aria-label={`Libellé tarif spécial ${index + 1}`}
                            className="w-full bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold tracking-[0.04em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary placeholder:text-brand-primary/40"
                            placeholder="Ex: Tarif réduit"
                            value={specialRate.label}
                            onChange={(e) => updateSpecialRate(specialRate.rateId, { label: e.target.value })}
                          />
                        </div>

                        <div className="group">
                          <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                            Prix (€)
                          </label>
                          <div className="relative">
                            <Euro className="absolute left-0 top-4 text-brand-accent opacity-60" size={18} />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              aria-label={`Prix tarif spécial ${index + 1}`}
                              className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                              value={specialRate.unitPrice}
                              onChange={(e) => updateSpecialRate(specialRate.rateId, { unitPrice: normalizePriceInput(e.target.value) })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16">
              <div className="group">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Lieu de l'expérience</label>
                <div className="relative">
                  <MapPin className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                  <input 
                    required
                    type="text" 
                    className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/45 text-brand-primary"
                    placeholder="Ex: Forêt de Brocéliande"
                    value={formData.location}
                    onChange={e => setFormData({...formData, location: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="group">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Places disponibles (max)</label>
                  <HelpTrigger
                    articleId="outing.create.capacity"
                    category="outing.create"
                    label="Aide sur la capacité maximale"
                  />
                </div>
                <div className="relative">
                  <Users className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                  <input 
                    required
                    type="number" 
                    min="1"
                    className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-2xl font-serif italic focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                    value={formData.maxParticipants}
                    onChange={e => setFormData({...formData, maxParticipants: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            <div className="group">
              <div className="flex items-center justify-between gap-4 mb-4">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Point de rendez-vous (Google Maps)</label>
                <HelpTrigger
                  articleId="outing.create.meeting_point"
                  category="outing.create"
                  label="Aide sur le point de rendez-vous"
                />
              </div>
              <input 
                required
                type="url" 
                className="w-full bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.08em] focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/45 text-brand-primary"
                placeholder="https://goo.gl/maps/..."
                value={formData.meetingPoint}
                onChange={e => setFormData({...formData, meetingPoint: e.target.value})}
              />
            </div>

            <div className="group bg-brand-paper/50 p-8 rounded-[32px] border border-brand-primary/5 space-y-8">
              <div className="flex items-center justify-between gap-4">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-80 block text-brand-primary">
                  Accessibilité et accueil
                </label>
                <HelpTrigger
                  articleId="outing.create.accessibility"
                  category="outing.create"
                  label="Aide sur l’accessibilité et l’accueil"
                />
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <p className="text-[12px] md:text-sm font-semibold text-brand-primary">Adapté aux PMR</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="pmrAccessible"
                        checked={formData.pmrAccessible === true}
                        onChange={() => setBooleanField('pmrAccessible', true)}
                      />
                      Oui
                    </label>
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="pmrAccessible"
                        checked={formData.pmrAccessible === false}
                        onChange={() => setBooleanField('pmrAccessible', false)}
                      />
                      Non
                    </label>
                    {isEdit && typeof formData.pmrAccessible !== 'boolean' ? (
                      <span className="text-[11px] opacity-60 text-brand-primary">Non renseigné actuellement</span>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-[12px] md:text-sm font-semibold text-brand-primary">Adapté aux poussettes</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="strollerAccessible"
                        checked={formData.strollerAccessible === true}
                        onChange={() => setBooleanField('strollerAccessible', true)}
                      />
                      Oui
                    </label>
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="strollerAccessible"
                        checked={formData.strollerAccessible === false}
                        onChange={() => setBooleanField('strollerAccessible', false)}
                      />
                      Non
                    </label>
                    {isEdit && typeof formData.strollerAccessible !== 'boolean' ? (
                      <span className="text-[11px] opacity-60 text-brand-primary">Non renseigné actuellement</span>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-[12px] md:text-sm font-semibold text-brand-primary">Chiens acceptés (en laisse uniquement)</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="dogsAllowedOnLeash"
                        checked={formData.dogsAllowedOnLeash === true}
                        onChange={() => setBooleanField('dogsAllowedOnLeash', true)}
                      />
                      Oui
                    </label>
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-brand-primary">
                      <input
                        type="radio"
                        name="dogsAllowedOnLeash"
                        checked={formData.dogsAllowedOnLeash === false}
                        onChange={() => setBooleanField('dogsAllowedOnLeash', false)}
                      />
                      Non
                    </label>
                    {isEdit && typeof formData.dogsAllowedOnLeash !== 'boolean' ? (
                      <span className="text-[11px] opacity-60 text-brand-primary">Non renseigné actuellement</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="group bg-brand-paper/50 p-8 rounded-[32px] border border-brand-primary/5 space-y-8">
              <div className="flex items-center justify-between gap-4">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-80 block text-brand-primary">
                  Réservations de dernière minute
                </label>
                <HelpTrigger
                  articleId="outing.create.schedule"
                  category="outing.create"
                  label="Aide sur les réservations de dernière minute"
                />
              </div>

              <div className="space-y-6">
                <label className="inline-flex items-center gap-3 text-[12px] md:text-sm font-semibold text-brand-primary">
                  <input
                    type="checkbox"
                    aria-label="Autoriser les réservations de dernière minute"
                    checked={formData.allowLateBookings}
                    onChange={(e) => setFormData({...formData, allowLateBookings: e.target.checked})}
                  />
                  Autoriser les réservations de dernière minute
                </label>

                {formData.allowLateBookings ? (
                  <div className="group">
                    <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block text-brand-primary">
                      Fermer les réservations X heures avant le départ
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="23"
                        step="1"
                        aria-label="Heures avant fermeture"
                        className="w-full bg-transparent border-b border-brand-primary/20 py-6 text-2xl font-serif italic focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                        value={formData.lateBookingHoursBefore}
                        onChange={(e) => {
                          const nextValue = Number.parseInt(
                            e.target.value || String(DEFAULT_LATE_BOOKING_HOURS_BEFORE),
                            10
                          );
                          setFormData({
                            ...formData,
                            lateBookingHoursBefore: Number.isInteger(nextValue)
                              ? nextValue
                              : DEFAULT_LATE_BOOKING_HOURS_BEFORE,
                          });
                        }}
                      />
                    </div>
                    <p className="text-[11px] md:text-xs opacity-60 mt-2 text-brand-primary">
                      Doit être entre 1 et 23 heures. Défaut: 4 heures.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="group">
              <div className="flex items-center justify-between gap-4 mb-4">
                <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Modes de règlement acceptés</label>
                <HelpTrigger
                  articleId="outing.create.payment_setup"
                  category="outing.create"
                  label="Aide sur les moyens de paiement"
                />
              </div>
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <label className={`flex-1 flex items-center justify-between p-6 rounded-[24px] border cursor-pointer transition-all ${formData.paymentMethods.includes('mollie') ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-primary/20'}`}>
                  <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-brand-primary">Carte Bancaire (Mollie)</span>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={formData.paymentMethods.includes('mollie')}
                    onChange={() => togglePaymentMethod('mollie')}
                  />
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${formData.paymentMethods.includes('mollie') ? 'bg-brand-accent border-brand-accent' : 'border-brand-primary/20'}`}>
                    {formData.paymentMethods.includes('mollie') && <Check size={14} className="text-white" />}
                  </div>
                </label>
                <label className={`flex-1 flex items-center justify-between p-6 rounded-[24px] border cursor-pointer transition-all ${formData.paymentMethods.includes('bank_transfer') ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-primary/20'}`}>
                  <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-brand-primary">Virement Bancaire</span>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={formData.paymentMethods.includes('bank_transfer')}
                    onChange={() => togglePaymentMethod('bank_transfer')}
                  />
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${formData.paymentMethods.includes('bank_transfer') ? 'bg-brand-accent border-brand-accent' : 'border-brand-primary/20'}`}>
                    {formData.paymentMethods.includes('bank_transfer') && <Check size={14} className="text-white" />}
                  </div>
                </label>
                <label className={`flex-1 flex items-center justify-between p-6 rounded-[24px] border cursor-pointer transition-all ${formData.paymentMethods.includes('on_site') ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-primary/20'}`}>
                  <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-brand-primary">Sur place</span>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={formData.paymentMethods.includes('on_site')}
                    onChange={() => togglePaymentMethod('on_site')}
                  />
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${formData.paymentMethods.includes('on_site') ? 'bg-brand-accent border-brand-accent' : 'border-brand-primary/20'}`}>
                    {formData.paymentMethods.includes('on_site') && <Check size={14} className="text-white" />}
                  </div>
                </label>
              </div>

              {formData.paymentMethods.includes('bank_transfer') && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-brand-paper/50 p-8 rounded-[32px] border border-brand-primary/5 space-y-4"
                >
                  <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block text-brand-primary">Modèle de communication pour le virement</label>
                  <input 
                    type="text" 
                    className="w-full bg-white border border-brand-primary/10 px-6 py-4 rounded-full text-[12px] font-mono focus:outline-none focus:border-brand-accent transition-all text-brand-primary"
                    placeholder="YYMMDD - Lieu + #RESERVATION_ID# + #NB_PERSONNES#"
                    value={formData.bankTransferTemplate}
                    onChange={e => setFormData({...formData, bankTransferTemplate: e.target.value})}
                  />
                  <p className="text-[11px] md:text-xs opacity-60 leading-relaxed text-brand-primary">
                    Utilisez les balises <span className="font-bold">#RESERVATION_ID#</span> et <span className="font-bold">#NB_PERSONNES#</span> pour qu'elles soient remplacées automatiquement lors de la réservation.
                  </p>

                  <div className="pt-2 space-y-2">
                    <div className="flex items-center justify-between gap-4">
                      <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-80 block text-brand-primary">
                        Tarification
                      </label>
                      <div className="flex items-center gap-2">
                        <HelpTrigger
                          articleId="outing.create.pricing"
                          category="outing.create"
                          label="Aide sur la tarification"
                        />
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-55 text-brand-primary">
                          Devise EUR · version v1
                        </span>
                      </div>
                    </div>
                    <input
                      type="text"
                      className={`w-full bg-white border px-6 py-4 rounded-full text-[12px] font-mono focus:outline-none transition-all text-brand-primary ${hasIbanInput && !hasValidIbanInput ? 'border-red-300' : 'border-brand-primary/10 focus:border-brand-accent'}`}
                      placeholder="BE12 1234 1234 1234"
                      value={formData.bankAccountOverride}
                      onChange={(e) => setFormData({ ...formData, bankAccountOverride: e.target.value.toUpperCase() })}
                    />
                    <p className={`text-[11px] md:text-xs ${hasIbanInput && !hasValidIbanInput ? 'text-red-600' : 'opacity-60 text-brand-primary'}`}>
                      {hasIbanInput && !hasValidIbanInput
                        ? 'IBAN belge invalide'
                        : 'Format attendu : BE + 14 chiffres'}
                    </p>
                    {organizationDefaultBankAccount ? (
                      <p className="text-[11px] md:text-xs opacity-50 text-brand-primary">
                        Compte par defaut organisation : {organizationDefaultBankAccount}
                      </p>
                    ) : null}
                  </div>
                </motion.div>
              )}
            </div>

            <div className="group">
              <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-6 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Récit de l'expérience</label>
              <textarea 
                required
                className="w-full bg-brand-paper/30 p-10 rounded-[40px] border border-brand-primary/20 focus:outline-none focus:border-brand-accent transition-all min-h-[300px] font-serif text-2xl italic leading-relaxed text-brand-primary placeholder:text-brand-primary/40"
                placeholder="Décrivez l'immersion..."
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
              ></textarea>
            </div>

            <div className="group">
              <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 mb-4 block group-focus-within:opacity-100 transition-opacity text-brand-primary">Image de couverture</label>
              
              <div className="flex flex-col gap-6">
                <div className="relative">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    className="hidden"
                    id="image-upload"
                  />
                  <label 
                    htmlFor="image-upload" 
                    className={`flex justify-center items-center gap-4 w-full border-2 border-dashed border-brand-primary/30 rounded-[32px] py-8 cursor-pointer hover:bg-brand-paper transition-all text-brand-primary ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {imageUploading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-accent"></div>
                    ) : (
                      <>
                        <Upload className="text-brand-accent opacity-60" size={24} />
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-85">
                          Charger une image depuis votre appareil
                        </span>
                      </>
                    )}
                  </label>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] opacity-65 text-brand-primary flex-shrink-0">OU LIEN WEB</span>
                  <div className="h-px flex-1 bg-brand-primary/20" />
                </div>

                <div className="relative">
                  <ImageIcon className="absolute left-0 top-6 text-brand-accent opacity-60" size={20} />
                  <input 
                    type="url" 
                    className="w-full pl-10 bg-transparent border-b border-brand-primary/20 py-6 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/45 text-brand-primary"
                    placeholder="https://images.unsplash.com/..."
                    value={formData.imageUrl}
                    onChange={e => setFormData({...formData, imageUrl: e.target.value})}
                  />
                </div>

                {showsExternalImageWarning ? (
                  <p className="rounded-[24px] border border-amber-300/60 bg-amber-50 px-5 py-4 text-[12px] md:text-[13px] font-medium leading-relaxed text-amber-900">
                    Image externe : elle peut ne pas apparaître correctement dans les partages Facebook. Pour un rendu optimal, chargez l’image directement dans l’application.
                  </p>
                ) : null}
                
                {formData.imageUrl && (
                  <div className="mt-4 rounded-[40px] overflow-hidden border border-brand-primary/20 relative group">
                    <img src={formData.imageUrl} alt="Couverture" className="w-full h-64 object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="absolute top-4 right-4 bg-white/80 p-3 rounded-full hover:bg-white transition-colors border border-black/10 opacity-0 group-hover:opacity-100"
                    >
                      <X size={16} className="text-brand-primary" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-75 block mb-8 text-brand-primary">Équipement à prévoir</label>
              <div className="flex gap-6 mb-10">
                <input 
                  type="text" 
                  className="flex-1 bg-transparent border-b border-brand-primary/20 py-4 text-[15px] md:text-sm font-semibold uppercase tracking-[0.12em] focus:outline-none focus:border-brand-accent transition-all placeholder:text-brand-primary/45 text-brand-primary"
                  placeholder="AJOUTER UN ÉLÉMENT"
                  value={gearItem}
                  onChange={e => setGearItem(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addGear())}
                />
                <button 
                  type="button"
                  onClick={addGear}
                  className="w-16 h-16 bg-brand-primary text-white rounded-full flex items-center justify-center hover:bg-brand-accent transition-all shadow-xl"
                >
                  <Plus size={24} />
                </button>
              </div>
              <div className="flex flex-wrap gap-4">
                {formData.requiredGear.map((item, i) => (
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    key={i} 
                    className="bg-brand-paper border border-brand-primary/5 px-8 py-4 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-4 text-brand-primary min-h-[40px]"
                  >
                    {item}
                    <button type="button" onClick={() => removeGear(i)} className="opacity-30 hover:opacity-100 transition-opacity"><X size={14} /></button>
                  </motion.span>
                ))}
              </div>
            </div>
          </div>

          <button 
            disabled={loading}
            type="submit" 
            className="w-full bg-brand-primary text-white py-8 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] hover:bg-brand-accent transition-all duration-500 disabled:opacity-50 mt-16 shadow-2xl min-h-[48px]"
          >
            {loading ? 'SYNCHRONISATION...' : isEdit ? 'METTRE À JOUR L\'EXPÉDITION' : 'ENREGISTRER LE BROUILLON'}
          </button>
        </form>
      </motion.div>

      <ConfirmDialog
        open={showUnsavedChangesDialog}
        title="Modifications non sauvegardées"
        message="Vous avez modifié cette expédition. Vous pouvez enregistrer vos changements ou les ignorer avant de quitter cet écran."
        confirmLabel="Sauvegarder"
        cancelLabel="Continuer l'édition"
        onConfirm={handleSaveFromDialog}
        onClose={handleCloseUnsavedChangesDialog}
      >
        <button
          type="button"
          onClick={handleDiscardChanges}
          className="px-5 py-3 rounded-full border border-red-200 text-sm font-bold tracking-[0.12em] uppercase text-red-700 hover:bg-red-50 transition-all"
        >
          Ignorer les modifications
        </button>
      </ConfirmDialog>
    </div>
  );
}
